/**
 * @typedef { import('./reactive-controller.js').ReactiveController } ReactiveController
 * @typedef { import('./reactive-controller.js').ReactiveControllerHost } ReactiveControllerHost
 */

/**
 * Element focus-within `ReactiveController`.
 * Exposes `value` property indicating that the element or one of it's descendants has gained focus,
 * and triggers element update when changed.
 * @implements ReactiveController
 */
export class FocusWithinController {
  /**
   * Constructor
   * @param { ReactiveControllerHost & HTMLElement } host
   * @param { { onChanged?: () => void } } [options]
   */
  constructor(host, options = {}) {
    (this._host = host).addController(this);
    /** @type { boolean } - Flag indicating that the host or one of it's descendants has gained focus */
    this.value = false;
    this._onChanged = options.onChanged ?? (() => host.requestUpdate());
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
  }

  hostConnected() {
    // useCapture to
    this._host.addEventListener('focus', this._onFocus, true);
    this._host.addEventListener('blur', this._onBlur, true);
  }

  /**
   * Handle 'focus' event
   * @param { FocusEvent } event
   */
  _onFocus(event) {
    if (!this.value) {
      this.value = true;
      this._onChanged.call(this._host);
    }
  }

  /**
   * Handle 'blur' event
   * @param { FocusEvent } event
   */
  _onBlur(event) {
    if (!this._host.contains(/** @type { Node | null } */ (event.relatedTarget))) {
      this.value = false;
      this._onChanged.call(this._host);
    }
  }

  hostDisconnected() {
    this._host.removeEventListener('focus', this._onFocus, true);
    this._host.removeEventListener('blur', this._onBlur, true);
  }
}
